import React, { useState, useRef, useEffect } from 'react'; // ✅ add useRef
import { useSIP } from '../../context/SIPContext';
import IncomingCallPopup from '../../Popups/WebRtc/IncomingCallPopup';
import ActiveCallPopup from '../../Popups/WebRtc/ActiveCallPopup';
import { useDraggable } from '../../hooks/useDraggable';
import Popup from 'reactjs-popup';
import CallFeedback from '../Feedbacks/Feedback';
import useFetch from '../../hooks/useFetch';
import useAuth from '../../hooks/useAuth';
import UseAxiosPrivate from '../../hooks/useAxiosPrivate';
import { FEEDBACK_DETAILS, GET_FB_DATA_URL } from '../../utils/ApiUrls';
import DraggablePopup from './DraggablePopup';

// ✅ Keep rest of your component exactly the same
const WebRtcIncomingCallHandler = ({ formConfig }) => {
    const {
        callStatus, caller, callerNumber, callDirection, answer, reject, hangup, hold, unhold, mute, unmute, isHeld, isMuted,
        isDialerOpen, closeDialer, makeCall, eventId,
        setCallStatus,
        session,setSession,
        setCaller,
        unregister,
        resetSip,
        reconnectSip,
        updateConfig,
    } = useSIP();
    const isOutgoingCall = callDirection === 'outgoing'; // true for dialpad-originated calls
    const { auth } = useAuth();
    const fetchData = useFetch();
    const axiosPrivate = UseAxiosPrivate("webrtc-feedback");
    const [showFeedback, setShowFeedback] = useState(false);
    const [customer, setCustomer] = useState({});
    const [campData, setCampData] = useState({});
    const [fbdata, setFbdata] = useState({
        id: "0",
        uid: "",
        agent_id: "",
        customer_id: "",
        answers: {},
        fbanswer: {},
        comment: "",
        folloup: "",
        dnc: false,
    });

    const feedbackRequestRef = useRef(0);
    const customerFetchedForRef = useRef(null); // caller value already looked up, to avoid refetching

    const fetchCustomerDetails = async () => {
        const campId = localStorage.getItem("camp_id");
        if (!campId) return null; // no active campaign - e.g. a follow-up call dialed outside the campaign flow

        const requestId = ++feedbackRequestRef.current;
        const callerNumber = localStorage.getItem('customerNumber'); // fallback to caller if customerNumber not set
        const campaignMode = localStorage.getItem('campaignMode');
        const phoneNumber = campaignMode === 'auto' || campaignMode === 'manual' ? callerNumber : caller;

        try {
            const payload = {
                camp_id: campId,
                phone: phoneNumber,
                admin_id: auth?.user_details?.parent,
            };
            const res = await fetchData("post", payload, FEEDBACK_DETAILS);

            if (feedbackRequestRef.current !== requestId) return null; // call ended/changed while fetching - discard

            setCustomer(res || {});
            setCampData(res?.campaign_details || {});
            customerFetchedForRef.current = caller;
            return res;
        } catch (err) {
            console.error("Failed to fetch customer details", err);
            return null;
        }
    };

    // Resolve the real customer number as soon as the call connects, since for
    // campaign calls the SIP caller identity can be the campaign's DID rather
    // than the customer's actual number (needed to pre-fill call transfer).
    useEffect(() => {
        if ((callStatus === 'calling' || callStatus === 'active') && caller && customerFetchedForRef.current !== caller) {
            fetchCustomerDetails();
        }
        // Skip while the feedback popup is open - it's still reading customer/campData
        // (Feedback01, and camp_data.feedBackLabels for Feedback03's options) to let the
        // agent finish feedback after hanging up.
        if (callStatus === 'idle' && !showFeedback) {
            customerFetchedForRef.current = null;
            setCustomer({});
            setCampData({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callStatus, caller, showFeedback]);

    const isFirstCallStatusRender = useRef(true);

    useEffect(() => {
        if (isFirstCallStatusRender.current) {
            isFirstCallStatusRender.current = false;
            return;
        }

        if (callStatus) {
            localStorage.setItem("callStatus", callStatus);
        }
    }, [callStatus]);


    useEffect(() => {
        if (isFirstCallStatusRender.current) {
            isFirstCallStatusRender.current = false;
            return;
        }

        if (session) {
            localStorage.setItem("session", session || "");
        }
    }, [session]);

    useEffect(() => {
        if (isFirstCallStatusRender.current) {
            isFirstCallStatusRender.current = false;
            return;
        }

        if (caller) {
            localStorage.setItem("caller", caller || "");
        }
    }, [caller]);

    const unRegisterReconnect = async () => {
        const wasUnregistered = await unregister();

        console.log('[sip] unregister confirmed:', wasUnregistered);

        await resetSip();

        const config = formConfig;

        if (config) {
            updateConfig(config);
        }

        await reconnectSip(config);

    };
    
    useEffect(()=>{

        const reInintializeCallStateAfterRefresh = async () => {
            const status = localStorage.getItem("callStatus")
            setSession(localStorage.getItem("session") || null);
            setCaller(localStorage.getItem("caller") || '');
            await unRegisterReconnect();
            if(status){
                setCallStatus(status || "idle");
            }else{
                setCallStatus("idle");
            }
        }

        reInintializeCallStateAfterRefresh();

    },[])

    const openFeedback = async () => {
        const res = customerFetchedForRef.current === caller ? customer : await fetchCustomerDetails();
        // Customer lookup can fail (e.g. no camp_id for a follow-up call dialed
        // outside the campaign flow) - that's fine as long as we have an eventId
        // to pull feedback data from directly below.
        if (!res && !eventId) return;

        let feedbackData = {
            id: "0",
            uid: res?.uid || "",
            agent_id: "",
            customer_id: res?.id || "",
            answers: {},
            fbanswer: {},
            comment: "",
            folloup: "",
            dnc: false,
        };

        // Call was dialed from a follow-up row - pull up its existing feedback so the
        // follow-up date (and the rest of the form) can be edited instead of redone.
        if (eventId) {
            try {
                const agentId = auth?.user_details?.type === "agent" ? (auth?.user_details?.contact_agent_id || 0) : 0;
                const response = await axiosPrivate.get(GET_FB_DATA_URL, {
                    params: { calluid: eventId, agent_id: agentId },
                });
                const fbObj = response?.data?.fobj_data;
                const customerData = response?.data?.customer_data;
                const campaignData = response?.data?.camp_data;

                // Refresh customer/campaign state from this call's actual data (feedback
                // labels, questions, customer details) instead of the earlier SIP lookup.
                if (customerData) setCustomer(customerData);
                if (campaignData) setCampData(campaignData);

                if (fbObj) {
                    feedbackData = {
                        id: fbObj.id ?? "0",
                        uid: fbObj.uid || res?.uid || "",
                        agent_id: fbObj.feedback_agent || "",
                        customer_id: fbObj.campaign_customer || customerData?.id || res?.id || "",
                        answers: fbObj.feedback_questions || {},
                        fbanswer: fbObj.feedback_labels || {},
                        comment: fbObj.comment || "",
                        folloup: fbObj.follow_up_date || new Date(),
                        dnc: customerData?.dnc || false,
                    };
                }
            } catch (err) {
                console.error("Failed to fetch existing feedback for follow-up update", err);
            }
        }

        setFbdata(feedbackData);
        setShowFeedback(true);
    };

    const closeFeedback = (value) => {
        setShowFeedback(value);
    };

    const feedbackPopup = (
        <Popup
            open={showFeedback}
            modal
            nested
            onClose={() => closeFeedback(false)}
            position="center center"
            contentStyle={{ width: "0px", marginTop: "-10px" }}
        >
            {(close) => (
                <CallFeedback
                    setfback={closeFeedback}
                    close={close}
                    customer={customer}
                    call_id={fbdata.uid}
                    camp_data={campData}
                    FBdata={fbdata}
                    setFBdata={setFbdata}
                />
            )}
        </Popup>
    );

    let callUi = null;

    if (callStatus === 'incoming') {
        callUi = (
            <IncomingCallPopup
                caller={caller}
                callerNumber={callerNumber}
                onAnswer={answer}
                onReject={reject}
            />
        );
    } else {
        // Single popup for the whole outbound flow: dialing (idle, opened from the
        // header dialpad icon), calling, and active — never more than one on screen.
        const showActiveCallPopup =
            callStatus === 'calling' || callStatus === 'active' || (callStatus === 'idle' && isDialerOpen);

        if (showActiveCallPopup) {
            const popupWidth = 420; // w-[420px]
            const popupHeight = 480; // approx card height
            callUi = (
                <DraggablePopup
                    initialPos={{
                        x: (window.innerWidth - popupWidth) / 2,
                        y: (window.innerHeight - popupHeight) / 2,
                    }}
                >
                    <ActiveCallPopup
                        caller={caller}
                        callerNumber={callerNumber}
                        customerNumber={customer?.customer_phone_number}
                        callStatus={callStatus}
                        isOutgoingCall={isOutgoingCall}
                        onCall={makeCall}
                        onClose={closeDialer}
                        onHangup={hangup}
                        onHold={hold}
                        onUnhold={unhold}
                        onMute={mute}
                        onUnmute={unmute}
                        isHeld={isHeld}
                        isMuted={isMuted}
                        onFeedback={openFeedback}
                    />
                </DraggablePopup>
            );
        }
    }

    return (
        <>
            {callUi}
            {feedbackPopup}
        </>
    );
};

export default WebRtcIncomingCallHandler;
